import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Viewer, Worker } from '@react-pdf-viewer/core';
import Select from 'react-select';
import { InputField } from '../../../shared/ui/input-field';
import packageJson from '../../../../package.json';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@app/store/store';
import { Controller, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

import { yupResolver } from '@hookform/resolvers/yup';
import { priceAddEditSchema } from '@features/catalog-add-edit/lib/validators';
import { selectCompanyList, fetchAllCompanies } from '../../../entities/company/model';
import {
  createPrice,
  fetchPriceById,
  updatePrice,
  deletePrice as deletePriceAction,
  selectCurrentPrice,
  selectPricesStatus,
} from '../../../entities/price/model';
import { Can } from '@shared/lib/ability';
import { Icon } from '../../../shared/assets/Icon';
import { ConfirmModal } from '@shared/ui/confirm-modal';

const pdfjsVersion = (packageJson.dependencies as Record<string, string>)['pdfjs-dist'].replace(/\^/g, '');

interface CustomPdfInputProps {
  onChange: (file: File) => void;
  viewPdf: (url: string) => void;
}

const CustomPdfInput = ({ onChange, viewPdf }: CustomPdfInputProps) => {
  const [fileName, setFileName] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile && selectedFile.type === 'application/pdf') {
      setFileName(selectedFile.name);
      onChange(selectedFile);
      const reader = new FileReader();
      reader.readAsDataURL(selectedFile);
      reader.onload = (e) => {
        viewPdf(e.target?.result as string);
      };
    }
  };

  return (
    <div className="flex flex-col gap-2 items-start">
      <input
        type="file"
        accept="application/pdf"
        className="hidden"
        id="pdf-input"
        onChange={handleFileChange}
      />
      <label
        htmlFor="pdf-input"
        className="px-4 py-2 bg-blue-600 text-white rounded cursor-pointer hover:bg-blue-700 transition-colors"
      >
        Выберите PDF
      </label>
      {fileName && <span className="text-sm">{fileName}</span>}
    </div>
  );
};

const PdfAddViewer = () => {
  const { id } = useParams<{ id: string }>();
  const status = useSelector(selectPricesStatus);
  const dispatch = useAppDispatch();
  const companies = useSelector(selectCompanyList);
  const currentPrice = useSelector(selectCurrentPrice);
  // Файл/превью PDF: на маунте currentPrice ещё пустой (fetch async),
  // поэтому стартуем с null и заполняем в эффекте ниже, когда
  // прайс приедет. Раньше тут стояла «жадная» инициализация
  // `useState(currentPrice?.file)`, и она читала null → PDF не
  // отображался при первом открытии.
  const [pdfFile, setPdfFile] = useState<File | string | null>(null);
  const [viewPdf, setViewPdf] = useState<string | null>(null);

  // useMemo, чтобы массив объектов опций пересчитывался строго при
  // изменении currentPrice/companies — иначе на каждом рендере
  // создавалась новая ссылка, и эффект ниже срабатывал бесконечно
  // (reset → перерисовка → новый массив → reset → …).
  const currentPriceCompanies = useMemo(
    () =>
      currentPrice
        ? companies
            .filter((company: any) => currentPrice.companies.includes(company.id))
            .map(({ id: cid, name }: any) => ({ value: cid, label: name }))
        : [],
    [currentPrice, companies]
  );

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(priceAddEditSchema),
    defaultValues: { name: '', companies: [] },
  });

  useEffect(() => {
    // Загрузка прайса и списка компаний. Раньше зависимостью был
    // только [dispatch] — при переходе с одной /prices/edit/{id1}
    // на /prices/edit/{id2} страница не перезапрашивала данные,
    // и форма оставалась с прежним прайсом. Добавили id.
    if (id) dispatch(fetchPriceById(Number(id)));
    dispatch(fetchAllCompanies());
  }, [dispatch, id]);

  useEffect(() => {
    // Главный фикс: reset выполняем, только когда асинхронный
    // fetchPriceById реально вернул данные текущего id (а не
    // оставшегося из предыдущей открытой страницы) И список
    // компаний тоже подтянулся — без companies в `companies`-поле
    // селекта нечего класть, и multi-select остаётся пустым.
    // Раньше эффект срабатывал один раз на маунте и рестартовал
    // форму с currentPrice=null → все поля оказывались пустыми.
    if (
      id &&
      currentPrice &&
      String(currentPrice.id) === String(id)
    ) {
      reset({
        ...currentPrice,
        companies: currentPriceCompanies,
      });
      // Заодно подставляем PDF в просмотрщик. currentPrice.file —
      // это URL, его можно отдать <Viewer> напрямую.
      if (currentPrice.file) {
        setPdfFile(currentPrice.file);
        setViewPdf(currentPrice.file);
      }
    }
  }, [id, currentPrice, currentPriceCompanies, reset]);

  const onSubmit = async (data: { name: string; companies?: any[] }) => {
    const formData = new FormData();
    formData.append('name', data.name);
    if (pdfFile instanceof File) formData.append('file', pdfFile);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (data.companies ?? []).forEach((company: any) => {
      formData.append('companies', String(company.value));
    });
    if (id) {
      dispatch(updatePrice({ id: Number(id), data: formData }));
    } else {
      dispatch(createPrice(formData));
    }
  };

  const deletePrice = (priceId: string) => {
    dispatch(deletePriceAction(Number(priceId)));
  };

  return (
    <div className="bg-white min-h-screen w-full rounded-[10px] p-5 relative">
      <Can I="change" a="pricelist">
        <Can I="add" a="pricelist">
          <div className="self-center flex-1 flex flex-col justify-end">
            <label className="block mb-1 font-medium">Выберите файл</label>
            <CustomPdfInput onChange={setPdfFile} viewPdf={setViewPdf} />
          </div>
          <form onSubmit={handleSubmit(onSubmit)}>
            <InputField
              id="name"
              placeholder="Название прайса"
              label="Название прайса"
              name="name"
              inputName="name"
              register={register}
              errors={errors}
            />
            <label className="block mb-1 font-medium mt-3">Выберите интеграторов</label>
            {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
            {(errors as any).selectedItems && (
              <div className="text-red-500 text-sm">{(errors as any).selectedItems.message}</div>
            )}
            <Controller
              name="companies"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  placeholder="Выберите интеграторов"
                  closeMenuOnSelect={false}
                  isMulti
                  options={companies.map((item: any) => ({
                    ...item,
                    value: item.id,
                    label: item.name,
                  }))}
                  onChange={(val) => field.onChange(val)}
                  className="group-select"
                />
              )}
            />
            <button
              type="submit"
              disabled={status === 'loading'}
              className="mt-3 px-4 py-2 bg-[#e3282a] text-white rounded hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              {status === 'loading'
                ? id ? 'Сохранение...' : 'Добавление...'
                : id ? 'Сохранить' : 'Добавить'}
            </button>
          </form>
        </Can>
      </Can>
      <Can I="delete" a="pricelist">
        {id && (
          // Иконка удаления приведена к общему дизайну: вместо
          // эмодзи 🗑 — стандартный <Icon name="trash-line" /> из
          // shared/assets (тот же спрайт используется в корзине).
          // Кастомный inline-попап подтверждения заменён на
          // глобальный <ConfirmModal> — он же используется на
          // страницах Companies, Prices, LegalEntities, и поэтому
          // диалог удаления выглядит везде одинаково.
          <div className="absolute top-4 right-4">
            <ConfirmModal
              danger
              openButton={
                <button
                  type="button"
                  className="p-2 text-gray-400 hover:text-[#e3282a] transition-colors rounded-md hover:bg-gray-100"
                  title="Удалить прайс"
                  aria-label="Удалить прайс"
                >
                  <Icon name="trash-line" size={20} />
                </button>
              }
              confirm={() => deletePrice(id)}
              texts={{
                title: 'Удаление прайса',
                text: 'Удалить этот прайс? Связи с компаниями тоже будут сняты.',
                cancel: 'Отмена',
                confirm: 'Удалить',
              }}
            />
          </div>
        )}
      </Can>
      <Can I="view" a="pricelist">
        {viewPdf && (
          <Worker workerUrl={`https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsVersion}/pdf.worker.min.js`}>
            <Viewer fileUrl={viewPdf} />
          </Worker>
        )}
      </Can>
    </div>
  );
};

export default PdfAddViewer;
